/**
 * Reliable in-page section scrolling for the landing page.
 *
 * Why this exists
 * ---------------
 * Below-the-fold landing sections are wrapped in <DeferredRender>, which keeps
 * the real content out of the DOM until an IntersectionObserver fires and
 * meanwhile reserves an approximate `minHeight` spacer. The anchor id lives on
 * the spacer, so `getElementById(id)` resolves — but at a position that is
 * hundreds to thousands of pixels away from where the section ends up once the
 * deferred content actually mounts.
 *
 * A plain `scrollIntoView()` therefore animates towards a stale offset: the
 * sections mount mid-flight, the page grows underneath the scroll, and the
 * visitor is dropped in unrelated content. It only "works" after the visitor
 * has scrolled once, because by then everything is mounted and offsets are
 * stable.
 *
 * The fix: ask every deferred section to mount first, wait for the layout to
 * settle, then scroll to the target's final position.
 */

/** Deferred sections listen for this and mount immediately. */
export const MOUNT_DEFERRED_EVENT = "myelina:mount-deferred";

/** Ask every <DeferredRender> on the page to mount right now. */
export function mountDeferredSections(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(MOUNT_DEFERRED_EVENT));
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function absoluteTop(el: Element): number {
  return Math.round(el.getBoundingClientRect().top + window.scrollY);
}

export interface ScrollToSectionOptions {
  /** Pixels of breathing room above the section (e.g. a sticky navbar). */
  offset?: number;
  /** Force a jump instead of a smooth animation. */
  behavior?: ScrollBehavior;
  /** How long to keep correcting for late-arriving layout, in ms. */
  settleMs?: number;
}

/** Frames the target must hold still before we consider the layout settled. */
const STABLE_FRAMES = 8;

/** Never stop correcting before this, so late lazy chunks are still caught. */
const MIN_SETTLE_MS = 900;

/** How long to wait for a lazily-rendered anchor id to appear in the DOM. */
const APPEAR_TIMEOUT_MS = 1000;



/**
 * True while at least one <DeferredRender> is still showing its spacer. Until
 * every one of them has mounted, section offsets are still going to move.
 */
function deferredSectionsPending(): boolean {
  return document.querySelector('[data-deferred-state="waiting"]') !== null;
}


/**
 * Scroll to an in-page section by id, reliably on the very first click from a
 * cold page load.
 *
 * Deferred sections mount asynchronously (React render + a lazy `import()` for
 * the chunk), so the target's offset keeps moving for several hundred ms after
 * the click. Rather than guessing when that finishes, we start scrolling
 * immediately and then re-issue the scroll every time the target moves, until
 * it holds still or the settle window expires. The visitor sees an immediate,
 * continuous scroll that ends on the real section.
 *
 * The correction loop bows out the moment the visitor scrolls by hand, so we
 * never fight their input.
 *
 * Resolves true when a target existed and was scrolled to.
 */
export function scrollToSection(
  id: string,
  { offset = 0, behavior, settleMs = 3000 }: ScrollToSectionOptions = {},
): Promise<boolean> {
  if (typeof document === "undefined") return Promise.resolve(false);

  // Mount every deferred section FIRST. Several anchors (#principles, #faq)
  // live inside lazy chunks, so their id does not exist in the DOM at all on a
  // cold load — resolving the target before mounting would no-op forever.
  mountDeferredSections();

  const resolvedBehavior: ScrollBehavior =
    behavior ?? (prefersReducedMotion() ? "auto" : "smooth");

  const scrollTop = (top: number, mode: ScrollBehavior) => {
    if (typeof window.scrollTo !== "function") return;
    try {
      window.scrollTo({ top, behavior: mode });
    } catch {
      window.scrollTo(0, top);
    }
  };

  const desiredTop = () => {
    const el = document.getElementById(id);
    return el ? Math.max(0, absoluteTop(el) - offset) : null;
  };

  /** Wait for the anchor to appear once its lazy chunk renders. */
  const waitForTarget = (): Promise<number | null> => {
    const immediate = desiredTop();
    if (immediate !== null) return Promise.resolve(immediate);
    if (typeof requestAnimationFrame !== "function") return Promise.resolve(null);

    return new Promise((resolve) => {
      const giveUpAt = Date.now() + APPEAR_TIMEOUT_MS;
      const poll = () => {
        const top = desiredTop();
        if (top !== null) return resolve(top);
        if (Date.now() > giveUpAt) return resolve(null);
        requestAnimationFrame(poll);
      };
      requestAnimationFrame(poll);
    });
  };

  return waitForTarget().then((initialTop) => {
    if (initialTop === null) return false;
    // Kick the scroll off as soon as the target exists.
    scrollTop(initialTop, resolvedBehavior);

    if (typeof requestAnimationFrame !== "function") return true;
    return runCorrectionLoop(initialTop);
  });

  function runCorrectionLoop(initialTop: number): Promise<boolean> {


  return new Promise((resolve) => {
    const startedAt = Date.now();
    const deadline = startedAt + settleMs;
    let lastTarget = initialTop;
    let stableFrames = 0;
    let userInterrupted = false;


    const onUserScroll = () => {
      userInterrupted = true;
    };
    const events: (keyof WindowEventMap)[] = ["wheel", "touchstart", "keydown"];
    events.forEach((evt) => window.addEventListener(evt, onUserScroll, { passive: true }));

    const finish = (result: boolean) => {
      events.forEach((evt) => window.removeEventListener(evt, onUserScroll));
      resolve(result);
    };

    const tick = () => {
      if (userInterrupted) return finish(true);

      const top = desiredTop();
      if (top === null) return finish(false);

      if (Math.abs(top - lastTarget) > 1) {
        // Late-mounting content moved the section — chase it.
        lastTarget = top;
        stableFrames = 0;
        scrollTop(top, resolvedBehavior);
      } else if (deferredSectionsPending()) {
        // Holding still only because the deferred content has not landed yet.
        stableFrames = 0;
      } else {
        stableFrames += 1;
      }

      // A wrapper flips to "ready" as soon as React commits it, but its lazy
      // chunk renders a frame or two later — so a section can look settled
      // before its real content exists. Hold the loop open for a minimum
      // window so those late arrivals still get chased.
      const elapsed = Date.now() - startedAt;
      const settled = stableFrames >= STABLE_FRAMES && elapsed >= MIN_SETTLE_MS;

      if (settled || Date.now() > deadline) {
        // Final exact landing, in case the smooth animation was still in
        // flight when the target stopped moving.
        scrollTop(lastTarget, resolvedBehavior);
        return finish(true);
      }


      requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  });
  }
}


